"use client";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState } from "react";

// Helper to check if an error is a network error that should be handled gracefully
function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError) {
    const errorMessage = String(error).toLowerCase();
    return (
      errorMessage.includes("load failed") ||
      errorMessage.includes("failed to fetch") ||
      errorMessage.includes("networkerror") ||
      errorMessage.includes("network request failed")
    );
  }
  return false;
}

export function QueryProvider({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000, // 1 minute
            refetchOnWindowFocus: false,
            // Don't retry network errors - they're usually transient and will be retried on next refetch
            retry: (failureCount, error) => {
              // Don't retry network errors
              if (isNetworkError(error)) {
                return false;
              }
              // Default retry logic for other errors (max 3 retries)
              return failureCount < 3;
            },
          },
          mutations: {
            // Don't retry network errors in mutations either
            retry: (failureCount, error) => {
              if (isNetworkError(error)) {
                return false;
              }
              return failureCount < 1; // Only retry once for mutations
            },
            onError: (error) => {
              // Network errors are expected and don't need special handling
              if (!isNetworkError(error)) {
                console.error("Mutation error:", error);
              }
            },
          },
        },
      })
  );

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}
